import React, { useEffect, useRef, useState } from 'react';
import { Spin, Card, Button, Typography, List, Tag, Space, Popconfirm, message, Upload } from 'antd';
import type { RcFile } from 'antd/es/upload';
import { get, post, getFreshAuthToken } from '@partsbadger/utils';
import { ThreeDViewerFromStepFile } from '../../components/PartViewer/ThreeDViewer';
import UIStore from '../../stores/UIStore';
import { LoadingOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { PageHeader } from 'components/PageHeader';
import { API_ROOT as BASE_URL } from '@partsbadger/utils';
const { Title, Paragraph } = Typography;

type Id = string;
type StepStatus = 'queued' | 'analyzing' | 'ready' | 'error';
type StepItem = { id: Id; name: string; status: StepStatus };

const toId = (x: any): Id => String(x ?? '');

// Strictly sequential uploader (3D only) — blocks AntD network & runs our own
const Sequential3DUpload: React.FC<{
  acceptExts?: string[];         // e.g., ['step','stp','iges']
  maxSizeMB?: number;            // e.g., 200
  onUploaded: (id: string, name: string) => void;
}> = ({ acceptExts = ['step','stp','iges'], maxSizeMB = 200, onUploaded }) => {
  const [queue, setQueue] = useState<RcFile[]>([]);
  const running = useRef(false);
  const endpoint = BASE_URL + `/customer/step-files/`;

  const processQueue = async () => {
    if (running.current) return;
    running.current = true;

    while (true) {
      let next: RcFile | undefined;
      setQueue(prev => {
        next = prev[0];
        return prev.slice(1);
      });
      if (!next) break;

      try {
        const form = new FormData();
        form.append('file', next);

        const token = await getFreshAuthToken();
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: token ? { Authorization: `Token ${token}` } : {},
          body: form,
        });

        if (!res.ok) {
          const text = await res.text().catch(() => '');
          message.error(`Upload failed (${res.status}): ${text || next.name}`);
          continue;
        }

        const data = await res.json().catch(() => ({}));
        const id = toId(data?.id);
        if (!id) {
          message.error('Upload succeeded but response had no id.');
          continue;
        }
        onUploaded(id, next.name);
      } catch (e: any) {
        message.error(`Upload error: ${e?.message || 'unknown error'}`);
      }
    }

    running.current = false;
  };

  // Only enqueue; never let AntD upload
  const beforeUpload = (file: RcFile, fileList: RcFile[]) => {
    // size guard
    if (maxSizeMB && file.size / 1024 / 1024 > maxSizeMB) {
      message.error(`File must be smaller than ${maxSizeMB}MB!`);
      return Upload.LIST_IGNORE;
    }
    // extension guard
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ext || !acceptExts.includes(ext)) {
      message.error('The file type is not allowed. Allowed: STEP, STP, IGES');
      return Upload.LIST_IGNORE;
    }

    setQueue(prev => [...prev, file, ...fileList.filter(f => f !== file)]);
    // kick the processor (safe to call repeatedly)
    setTimeout(processQueue, 0);
    // prevent AntD from sending the request
    return Upload.LIST_IGNORE;
  };

  const acceptAttr = acceptExts.map(e => `.${e}`).join(',');

  return (
    <Upload.Dragger
      name="file"
      multiple
      showUploadList={false}
      beforeUpload={beforeUpload}
      accept={acceptAttr}
    >
      <div className="flex flex-col justify-center items-center" style={{ height: '309px' }}>
        <UploadOutlined style={{ fontSize: '96px', color: '#405764' }} />
        <p className="font-bold text-2xl  text-center pt-6" style={{ width: '227px' }}>
          Select STEP, STP or IGES File
        </p>
        <p className="text-base font-bold ml-1  pt-10" style={{ color: '#595959' }}>
          Drop your files here or <span className="text-red-600 font-bold">Browse</span>
        </p>
      </div>
    </Upload.Dragger>
  );
};

const Part3DViewer: React.FC = () => {
  const [items, setItems] = useState<StepItem[]>([]);
  const [activeId, setActiveId] = useState<Id | undefined>(undefined);
  const [spinning, setSpinning] = useState(false);

  const processedIds = useRef(new Set<Id>());
  const pollers = useRef<Record<Id, number | undefined>>({});

  useEffect(() => {
    UIStore.setSelectedKey(['part-viewer']);
    UIStore.setDocumentTitle('Part Viewer');
    return () => {
      Object.values(pollers.current).forEach(t => t && clearInterval(t));
      pollers.current = {};
    };
  }, []);

  const startAnalyzeAndPoll = async (fileId: Id, fileName: string) => {
    setItems(prev => {
      const exists = prev.some(p => p.id === fileId);
      const next: StepItem[] = exists
        ? prev.map(p => (p.id === fileId ? { ...p, status: 'analyzing' } as StepItem : p))
        : [...prev, { id: fileId, name: fileName, status: 'analyzing' as StepStatus }];
      return next;
    });

    setActiveId(prev => prev ?? fileId);
    setSpinning(true);

    try {
      await post(`/customer/step-files/${fileId}/analyze/`);
    } catch (e) {
      setItems(prev => prev.map(p => (p.id === fileId ? { ...p, status: 'error' } as StepItem : p)));
      setSpinning(items.some(i => i.status === 'analyzing'));
      return;
    }

    const timer = window.setInterval(async () => {
      try {
        const data = await get(`/customer/step-files/${fileId}/status/`);
        if (data?.completed || data?.ready) {
          clearInterval(timer);
          pollers.current[fileId] = undefined;
          setItems(prev => {
            const next = prev.map(p => (p.id === fileId ? { ...p, status: 'ready' } as StepItem : p));
            setSpinning(next.some(i => i.status === 'analyzing'));
            return next;
          });
        }
      } catch {
        clearInterval(timer);
        pollers.current[fileId] = undefined;
        setItems(prev => prev.map(p => (p.id === fileId ? { ...p, status: 'error' } as StepItem : p)));
        setSpinning(prev => false);
      }
    }, 2000);

    pollers.current[fileId] = timer;
  };

  // hook for the uploader to call after each successful upload
  const onUploaded = (id: string, name: string) => {
    if (processedIds.current.has(id)) return;
    processedIds.current.add(id);
    startAnalyzeAndPoll(id, name || `File ${id}`);
  };

  const removeItem = (id: Id) => {
    const t = pollers.current[id];
    if (t) clearInterval(t);
    delete pollers.current[id];

    processedIds.current.delete(id);

    setItems(prev => {
      const next = prev.filter(p => p.id !== id);
      if (activeId === id) {
        const nextReady = [...next].reverse().find(p => p.status === 'ready');
        const fallback = next[next.length - 1];
        setActiveId(nextReady?.id ?? fallback?.id);
      }
      return next;
    });
  };

  const clearAll = () => {
    Object.values(pollers.current).forEach(t => t && clearInterval(t));
    pollers.current = {};
    processedIds.current.clear();
    setItems([]);
    setActiveId(undefined);
    setSpinning(false);
  };

  const handleViewClick = (item: StepItem) => {
    if (item.status === 'error') {
      message.error('This file failed to analyze');
      return;
    }
    if (item.status !== 'ready') {
      message.info('File is still analyzing, please wait...');
      return;
    }
    setActiveId(item.id);
  };

  const statusTag = (status: StepStatus) => {
    switch (status) {
      case 'queued':
        return <Tag>Queued</Tag>;
      case 'analyzing':
        return <Tag color="processing">Analyzing</Tag>;
      case 'ready':
        return <Tag color="success">Ready</Tag>;
      case 'error':
        return <Tag color="error">Error</Tag>;
    }
  };

  const viewerId = activeId ? Number(activeId) : undefined;
  const activeItem = items.find(i => i.id === activeId);

  return (
    <>
      <PageHeader>
        <div className="d-flex justify-content-between align-items-baseline mb-0">
          <div>
            <Title level={1}>3D File Viewer</Title>
            <Paragraph className="mb-0">Upload and view your 3D files here</Paragraph>
          </div>
          <Space>
            {activeId && (
              <Button size="small" type="primary" onClick={e => { e.preventDefault(); setActiveId(undefined); }}>
                Close 3D File
              </Button>
            )}
            {items.length > 0 && (
              <Popconfirm title="Clear all uploaded files?" onConfirm={clearAll}>
                <Button size="small" danger icon={<DeleteOutlined />}>Clear All</Button>
              </Popconfirm>
            )}
          </Space>
        </div>
      </PageHeader>

      <div className="container py-4">
        <Spin spinning={spinning} tip="Analyzing Geometry..." indicator={<LoadingOutlined />}>
          <Card bordered bodyStyle={{ padding: !activeId ? '1rem' : 0 }} className="shadow-sm">
            {!activeId ? (
              <Sequential3DUpload
                acceptExts={['step','stp','iges']}
                maxSizeMB={200}
                onUploaded={onUploaded}
              />
            ) : activeItem?.status === 'ready' && viewerId ? (
              <ThreeDViewerFromStepFile id={viewerId} />
            ) : (
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <p>
                  { activeItem?.status === 'error'
                    && 'This file failed to analyze'}
                </p>
              </div>
            )}
          </Card>
        </Spin>

        {items.length > 0 && (
          <Card className="shadow-sm mt-4" title={`Uploaded files (${items.length})`}>
            <List
              itemLayout="horizontal"
              dataSource={items}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    <Button
                      key="view"
                      type={activeId === item.id ? 'default' : 'primary'}
                      size="small"
                      onClick={() => handleViewClick(item)}
                      disabled={item.status === 'analyzing'}
                    >
                      {activeId === item.id ? 'Viewing' : 'View'}
                    </Button>,
                    <Popconfirm key="del" title="Remove this file?" onConfirm={() => removeItem(item.id)} okText="Yes" cancelText="No">
                      <Button size="small" danger>Remove</Button>
                    </Popconfirm>,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space size="small">
                        <span>{item.name}</span>
                        {statusTag(item.status)}
                        {activeId === item.id && <Tag color="blue">Active</Tag>}
                      </Space>
                    }
                    description={<span>ID: {item.id}</span>}
                  />
                </List.Item>
              )}
            />
          </Card>
        )}
      </div>
    </>
  );
};

export default Part3DViewer;
